/**
 * @file PlanYearTabsPage.js
 * @description Main component for managing plan tabs and handling plan data.
 */

import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './PlanYear.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import PlanOverviewTab from './tabs/PlanYearOverviewTab';
import PlanCustomTextTab from './tabs/PlanYearCustomTextTab';
import PlanFilesTab from './tabs/PlanYearFilesTab';
import {
    fetchPlanYearById,
    createPlanYear,
    updatePlanYear,
} from '../../services/planYearService';
import { deepEqual } from '../../utils/utils';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
const TABS = {
    OVERVIEW: 'Overview',
    CUSTOM_TEXT: getText('msg_3ed05dd7c27f'),
    FILES: 'Files',
};

const PlanTabsPage = ({ isCreating = false }) => {
    const { showModal } = useContext(ModalContext);
    const navigate = useNavigate();
    const { planYearId } = useParams();
    const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);
    const [planData, setPlanData] = useState({
        id: 0,
        name: '',
        code: '',
        planTypeId: null,
        vendorId: null,
        networkTypeId: null,
        policy: '',
        phoneNumber: '',
        url: '',
        importId: '',
        startDate: null,
        endDate: null,
        offCycle: false,
    });
    const [originalPlanData, setOriginalPlanData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (planYearId && !isCreating) {
            loadPlanData(planYearId);
        } else {
            setIsLoading(false);
        }
    }, [planYearId, isCreating]);

    const loadPlanData = async id => {
        try {
            setIsLoading(true);
            const fetchedPlan = await fetchPlanYearById(id);
            setPlanData(fetchedPlan);
            setOriginalPlanData(JSON.parse(JSON.stringify(fetchedPlan)));
        } catch (error) {
            console.error('Failed to fetch plan data', error);
            Toast.error(getText('msg_eea90eec1022'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabChange = tab => {
        setActiveTab(tab);
    };

    const handlePlanDataChange = updatedData => {
        if (updatedData._isDefaultStartDate) {
            const cleanData = { ...updatedData };
            delete cleanData._isDefaultStartDate;
            setPlanData(prevData => ({
                ...prevData,
                ...cleanData,
            }));
        } else {
            setPlanData(prevData => ({
                ...prevData,
                ...updatedData,
            }));
        }
    };

    const transformPlanYearAssets = assets => {
        if (!Array.isArray(assets)) {
            return [];
        }

        return assets.map(asset => {
            const populationGroupIds = Array.isArray(asset.populationGroupIds)
                ? asset.populationGroupIds
                : Array.isArray(asset.populationGroups)
                  ? asset.populationGroups
                        .map(group => {
                            if (typeof group === 'object' && group !== null) {
                                return group.populationGroupId || group.id;
                            }
                            return group;
                        })
                        .filter(Boolean)
                        .map(id => id.toString())
                  : [];

            return {
                ...asset,
                populationGroupIds,
            };
        });
    };

    const validatePlanData = () => {
        if (!planData.name) {
            Toast.error(getText('msg_09cdd83c9272'));
            setActiveTab(TABS.OVERVIEW);
            return false;
        }

        if (!planData.code) {
            Toast.error(getText('msg_d0cf451a4482'));
            setActiveTab(TABS.OVERVIEW);
            return false;
        }

        if (!planData.startDate) {
            Toast.error(getText('msg_438387af9072'));
            setActiveTab(TABS.OVERVIEW);
            return false;
        }

        if (!planData.endDate) {
            Toast.error(getText('msg_0f750cd59cac'));
            setActiveTab(TABS.OVERVIEW);
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        if (!validatePlanData()) {
            return;
        }

        try {
            setIsSaving(true);

            const transformedPlanData = {
                ...planData,
                planYearAssets: transformPlanYearAssets(
                    planData.planYearAssets,
                ),
            };

            if (isCreating || !planYearId) {
                await createPlanYear(transformedPlanData);
                Toast.success(getText('msg_42801b65892b'));
            } else {
                await updatePlanYear(transformedPlanData);
                Toast.success(getText('msg_b614f2bd9dd8'));
                setOriginalPlanData(transformedPlanData);
            }
        } catch (error) {
            console.error('Failed to save plan year', error);
            const errorMessage =
                error.response?.data?.detail ||
                'There was an issue saving the plan year.';
            Toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (checkUnsavedChanges()) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: () => navigate(ROUTES.PLAN_YEAR),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_36fff63ccbcd'),
            });
        } else {
            navigate(ROUTES.PLAN_YEAR);
        }
    };

    const checkUnsavedChanges = () => {
        if (!originalPlanData) {
            if (!isCreating) return false;

            return (
                !!planData.name ||
                !!planData.code ||
                !!planData.startDate ||
                !!planData.endDate ||
                !!planData.policy
            );
        }

        return !deepEqual(planData, originalPlanData);
    };

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center position-relative pb-3 flex-column flex-xl-row gap-2">
                        <h2 className="page-title float-start ms-4 m-0 col-auto col-lg-2 text-nowrap align-self-xl-baseline">
                            {isCreating ? 'Add Plan Year' : planData.name}
                        </h2>
                        <div
                            className={`${styles.fitContentWidth} col-auto col-lg-2 d-flex me-3 align-self-xl-baseline justify-content-end`}
                        >
                            {isSaving ? (
                                <span className={styles.ltrText}>
                                    {getText('msg_ae7e887517b0')}
                                </span>
                            ) : (
                                <div className="float-end d-flex align-items-center align-self-baseline">
                                    <span
                                        type="button"
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className="ing-text-buttons mx-3"
                                    >
                                        <i className="bi bi-check me-2" />
                                        {getText('msg_efc007a393f6')}
                                    </span>
                                    <span
                                        type="button"
                                        onClick={handleClose}
                                        disabled={isSaving}
                                        className="ing-text-buttons mx-3"
                                    >
                                        <i className="bi bi-x me-2" />
                                        {getText('msg_bbfa773e5a63')}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center position-relative mt-5">
                        <ul className={`col-md-8 col-lg-8 ${styles.tabList}`}>
                            {Object.values(TABS).map(tab => (
                                <li
                                    key={tab}
                                    className={`${styles.tabItem} ${
                                        activeTab === tab ? styles.active : ''
                                    }`}
                                    onClick={() => handleTabChange(tab)}
                                >
                                    {tab}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {isLoading ? (
                        <div className="w-100 mt-5 pb-5 text-center">
                            <Spinner
                                Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        </div>
                    ) : (
                        <div
                            className={`w-100 mt-5 pb-5 ${styles.tabContainer}`}
                        >
                            {activeTab === TABS.OVERVIEW && (
                                <PlanOverviewTab
                                    data={planData}
                                    onChange={handlePlanDataChange}
                                    onSave={handleSave}
                                />
                            )}
                            {activeTab === TABS.CUSTOM_TEXT && (
                                <PlanCustomTextTab
                                    data={planData}
                                    onChange={handlePlanDataChange}
                                    onSave={handleSave}
                                />
                            )}
                            {activeTab === TABS.FILES && (
                                <PlanFilesTab
                                    data={planData}
                                    onChange={handlePlanDataChange}
                                    onSave={handleSave}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PlanTabsPage;
